import { useState, useMemo, useEffect } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import {
  usePersonalTransactions,
  type TransactionWithCategory,
  type TransactionType,
  type CreateTransactionInput,
  type UpdateTransactionInput,
} from '@/hooks';
import { usePersonalCategories, type PersonalCategory } from '@/hooks';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  ArrowUpCircle,
  ArrowDownCircle,
  Receipt,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// ─── Constants ───────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Types ───────────────────────────────────────────────────────────────

type DialogMode = 'create' | 'edit' | null;

interface TxFormState {
  transaction_type: TransactionType;
  amount: string;
  description: string;
  personal_category_id: string;
  transaction_date: string;
  reference_number: string;
  receipt_notes: string;
}

const EMPTY_FORM: TxFormState = {
  transaction_type: 'expense',
  amount: '',
  description: '',
  personal_category_id: '',
  transaction_date: todayISO(),
  reference_number: '',
  receipt_notes: '',
};

// ─── Helper: flatten categories with display names ──────────────────────

type FlatCategory = PersonalCategory & { displayName: string };

function flattenCategories(categories: PersonalCategory[]): FlatCategory[] {
  const result: FlatCategory[] = [];
  for (const cat of categories) {
    result.push({ ...cat, displayName: cat.name });
    if (cat.children) {
      for (const child of cat.children) {
        result.push({ ...child, displayName: `${cat.name} > ${child.name}` });
      }
    }
  }
  return result;
}

// ─── TransactionRow ──────────────────────────────────────────────────────

function TransactionRow({
  tx,
  onEdit,
  onDelete,
}: {
  tx: TransactionWithCategory;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const amount = parseFloat(tx.amount);
  const isIncome = tx.transaction_type === 'income';

  return (
    <div className="flex items-center gap-4 border-b border-border py-3 last:border-b-0">
      {/* Icon */}
      <div className="flex-shrink-0">
        {isIncome ? (
          <ArrowUpCircle className="h-5 w-5 text-sapling-primary-100" />
        ) : (
          <ArrowDownCircle className="h-5 w-5 text-destructive" />
        )}
      </div>

      {/* Date */}
      <div className="w-24 flex-shrink-0 text-sm text-muted-foreground">
        {formatDate(tx.transaction_date)}
      </div>

      {/* Description + category */}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-foreground">{tx.description}</p>
        <p className="text-xs text-muted-foreground">
          {tx.category_name ? (
            <span className="inline-flex items-center gap-1">
              {tx.category_color && (
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: tx.category_color }}
                />
              )}
              {tx.category_name}
            </span>
          ) : (
            <span className="italic">Uncategorized</span>
          )}
          {tx.reference_number && (
            <span className="ml-2 text-muted-foreground/60">#{tx.reference_number}</span>
          )}
        </p>
      </div>

      {/* Amount */}
      <div
        className={`w-28 flex-shrink-0 text-right font-mono text-sm font-medium ${
          isIncome ? 'text-sapling-primary-100' : 'text-destructive'
        }`}
      >
        {isIncome ? '+' : '-'}{formatCurrency(amount)}
      </div>

      {/* Actions */}
      <div className="flex flex-shrink-0 items-center gap-1">
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
}

// ─── PersonalTransactionsPage ────────────────────────────────────────────

export function PersonalTransactionsPage() {
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | TransactionType>('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);

  const filters = useMemo(
    () => ({
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      categoryId: categoryId || undefined,
      type: typeFilter || undefined,
      search: search || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [dateFrom, dateTo, categoryId, typeFilter, search, page]
  );

  const {
    transactions,
    total,
    isLoading,
    createTransaction,
    updateTransaction,
    deleteTransaction,
  } = usePersonalTransactions(filters);

  const { categories } = usePersonalCategories();
  const allCategories = useMemo(() => flattenCategories(categories), [categories]);

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingTx, setEditingTx] = useState<TransactionWithCategory | null>(null);
  const [form, setForm] = useState<TxFormState>(EMPTY_FORM);

  // Auto-open create dialog if ?action=add is in URL
  const searchParams = useSearch({ strict: false }) as Record<string, string>;
  useEffect(() => {
    if (searchParams.action === 'add') {
      setForm(EMPTY_FORM);
      setEditingTx(null);
      setDialogMode('create');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isSaving = createTransaction.isPending || updateTransaction.isPending;

  // Filter the category dropdown by transaction type
  const filteredCategories = useMemo(() => {
    return allCategories.filter((c) => {
      if (!c.is_active) return false;
      if (form.transaction_type === 'income') return c.category_type === 'income';
      if (form.transaction_type === 'expense') return c.category_type === 'expense';
      return true; // transfer shows all
    });
  }, [allCategories, form.transaction_type]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ── Dialog handlers
  function openCreate() {
    setForm(EMPTY_FORM);
    setEditingTx(null);
    setDialogMode('create');
  }

  function openEdit(tx: TransactionWithCategory) {
    setForm({
      transaction_type: tx.transaction_type,
      amount: parseFloat(tx.amount).toString(),
      description: tx.description,
      personal_category_id: tx.personal_category_id || '',
      transaction_date: tx.transaction_date,
      reference_number: tx.reference_number || '',
      receipt_notes: tx.receipt_notes || '',
    });
    setEditingTx(tx);
    setDialogMode('edit');
  }

  function closeDialog() {
    setDialogMode(null);
    setEditingTx(null);
  }

  async function handleSave() {
    const amount = parseFloat(form.amount);
    if (!form.description.trim()) {
      toast({ variant: 'destructive', title: 'Description is required' });
      return;
    }
    if (isNaN(amount) || amount <= 0) {
      toast({ variant: 'destructive', title: 'Amount must be a positive number' });
      return;
    }
    if (!form.transaction_date) {
      toast({ variant: 'destructive', title: 'Date is required' });
      return;
    }

    try {
      if (dialogMode === 'create') {
        const input: CreateTransactionInput = {
          transaction_type: form.transaction_type,
          amount,
          description: form.description.trim(),
          transaction_date: form.transaction_date,
          personal_category_id: form.personal_category_id || undefined,
          reference_number: form.reference_number.trim() || undefined,
          receipt_notes: form.receipt_notes.trim() || undefined,
        };
        await createTransaction.mutateAsync(input);
        toast({ title: 'Transaction added' });
      } else if (dialogMode === 'edit' && editingTx) {
        const input: UpdateTransactionInput & { id: string } = {
          id: editingTx.id,
          transaction_type: form.transaction_type,
          amount,
          description: form.description.trim(),
          transaction_date: form.transaction_date,
          personal_category_id: form.personal_category_id || null,
          reference_number: form.reference_number.trim() || null,
          receipt_notes: form.receipt_notes.trim() || null,
        };
        await updateTransaction.mutateAsync(input);
        toast({ title: 'Transaction updated' });
      }
      closeDialog();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  async function handleDelete(tx: TransactionWithCategory) {
    if (!confirm(`Delete "${tx.description}"? This cannot be undone.`)) return;
    try {
      await deleteTransaction.mutateAsync(tx.id);
      toast({ title: 'Transaction deleted' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  function handleSearch() {
    setSearch(searchInput);
    setPage(0);
  }

  function resetFilters() {
    setDateFrom('');
    setDateTo('');
    setCategoryId('');
    setTypeFilter('');
    setSearch('');
    setSearchInput('');
    setPage(0);
  }

  const hasFilters = dateFrom || dateTo || categoryId || typeFilter || search;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="container max-w-4xl">
          {/* Back link */}
          <Link
            to="/app/personal/dashboard"
            className="mb-6 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Personal Dashboard
          </Link>

          {/* Page header */}
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-2xl font-bold text-foreground">Transactions</h1>
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add Transaction
            </Button>
          </div>

          {/* Filters */}
          <div className="mb-6 rounded-card border bg-card p-4">
            <div className="flex flex-wrap items-end gap-3">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <Label htmlFor="filter-search" className="text-xs">Search</Label>
                <div className="relative">
                  <Input
                    id="filter-search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    placeholder="Search descriptions..."
                    className="pr-8"
                  />
                  <button
                    onClick={handleSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Type */}
              <div className="w-32">
                <Label htmlFor="filter-type" className="text-xs">Type</Label>
                <select
                  id="filter-type"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value as '' | TransactionType);
                    setPage(0);
                  }}
                  className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                >
                  <option value="">All</option>
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                  <option value="transfer">Transfer</option>
                </select>
              </div>

              {/* Category */}
              <div className="w-40">
                <Label htmlFor="filter-cat" className="text-xs">Category</Label>
                <select
                  id="filter-cat"
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    setPage(0);
                  }}
                  className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                >
                  <option value="">All</option>
                  {allCategories.filter((c) => c.is_active).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date range */}
              <div className="w-36">
                <Label htmlFor="filter-from" className="text-xs">From</Label>
                <Input
                  id="filter-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(0);
                  }}
                />
              </div>
              <div className="w-36">
                <Label htmlFor="filter-to" className="text-xs">To</Label>
                <Input
                  id="filter-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(0);
                  }}
                />
              </div>

              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Empty state */}
          {!isLoading && transactions.length === 0 && (
            <div className="mt-8 rounded-card border border-dashed bg-muted/50 p-8 text-center">
              <Receipt className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="mb-1 font-medium text-foreground">
                {hasFilters ? 'No transactions match your filters' : 'No transactions yet'}
              </p>
              <p className="mb-4 text-sm text-muted-foreground">
                {hasFilters
                  ? 'Try adjusting your filters or clearing them.'
                  : 'Add your first transaction to start tracking your finances.'}
              </p>
              {!hasFilters && (
                <Button onClick={openCreate} variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Transaction
                </Button>
              )}
            </div>
          )}

          {/* Transaction list */}
          {!isLoading && transactions.length > 0 && (
            <>
              <div className="rounded-card border bg-card p-4">
                {transactions.map((tx) => (
                  <TransactionRow
                    key={tx.id}
                    tx={tx}
                    onEdit={() => openEdit(tx)}
                    onDelete={() => handleDelete(tx)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage(page - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* ── Create / Edit Dialog ─────────────────────────────────────── */}
      <Dialog
        open={dialogMode !== null}
        onOpenChange={(open) => { if (!open) closeDialog(); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' ? 'Add Transaction' : 'Edit Transaction'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Type toggle */}
            <div>
              <Label>Type</Label>
              <div className="mt-1 flex gap-2">
                {(['expense', 'income', 'transfer'] as TransactionType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() =>
                      setForm({ ...form, transaction_type: t, personal_category_id: '' })
                    }
                    className={`rounded-lg border px-4 py-1.5 text-sm capitalize transition-colors ${
                      form.transaction_type === t
                        ? t === 'income'
                          ? 'border-sapling-primary-100 bg-sapling-primary-100/10 text-sapling-primary-100'
                          : t === 'expense'
                            ? 'border-destructive bg-destructive/10 text-destructive'
                            : 'border-sapling-secondary-100 bg-sapling-secondary-100/10 text-sapling-secondary-100'
                        : 'border-border hover:bg-muted'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div>
              <Label htmlFor="tx-amount">Amount ($)</Label>
              <Input
                id="tx-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="tx-desc">Description</Label>
              <Input
                id="tx-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Grocery shopping"
              />
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="tx-category">Category</Label>
              <select
                id="tx-category"
                value={form.personal_category_id}
                onChange={(e) =>
                  setForm({ ...form, personal_category_id: e.target.value })
                }
                className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
              >
                <option value="">None</option>
                {filteredCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="tx-date">Date</Label>
              <Input
                id="tx-date"
                type="date"
                value={form.transaction_date}
                onChange={(e) => setForm({ ...form, transaction_date: e.target.value })}
              />
            </div>

            {/* Reference */}
            <div>
              <Label htmlFor="tx-ref">Reference # (optional)</Label>
              <Input
                id="tx-ref"
                value={form.reference_number}
                onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                placeholder="Check #, invoice #, etc."
              />
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="tx-notes">Notes (optional)</Label>
              <Input
                id="tx-notes"
                value={form.receipt_notes}
                onChange={(e) => setForm({ ...form, receipt_notes: e.target.value })}
                placeholder="Additional notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {dialogMode === 'create' ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
